import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Check, Code2, Copy } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const LANGUAGE_LABELS = {
  bash: 'Bash',
  c: 'C',
  cpp: 'C++',
  css: 'CSS',
  html: 'HTML',
  java: 'Java',
  javascript: 'JavaScript',
  js: 'JavaScript',
  json: 'JSON',
  jsx: 'JSX',
  markdown: 'Markdown',
  md: 'Markdown',
  objectivec: 'Objective-C',
  php: 'PHP',
  python: 'Python',
  py: 'Python',
  ruby: 'Ruby',
  rust: 'Rust',
  shell: 'Shell',
  sh: 'Shell',
  sql: 'SQL',
  swift: 'Swift',
  text: 'Text',
  ts: 'TypeScript',
  tsx: 'TSX',
  typescript: 'TypeScript',
  xml: 'XML',
  yaml: 'YAML',
  yml: 'YAML',
};

const getLanguageLabel = (language) => {
  if (!language) return '';

  const normalized = language.toLowerCase().replace(/[^a-z0-9]/g, '');
  return LANGUAGE_LABELS[normalized]
    || language
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
};

const CodeBlock = ({ inline, className, children, ...props }) => {
  const [copyStatus, setCopyStatus] = useState('idle');
  const { effectiveTheme } = useTheme() || {};

  const match = /language-([^\s]+)/.exec(className || '');
  const language = match?.[1] || '';
  const rawCodeString = String(children);
  const isCodeBlock = Boolean(language) || rawCodeString.includes('\n');
  const syntaxTheme = effectiveTheme === 'dark' ? oneDark : oneLight;

  if (isCodeBlock) {
    const codeString = rawCodeString.replace(/\n$/, '');
    const languageLabel = getLanguageLabel(language);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(codeString);
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('idle'), 2000);
      } catch (err) {
        setCopyStatus('failed');
        setTimeout(() => setCopyStatus('idle'), 2000);
      }
    };

    return (
      <div className={`arrow-code-block ${languageLabel ? '' : 'arrow-code-block--plain'}`}>
        {languageLabel ? (
          <div className="arrow-code-header">
            <div className="arrow-code-language">
              <Code2 size={16} aria-hidden="true" />
              <span>{languageLabel}</span>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="arrow-code-copy-button"
              aria-label={copyStatus === 'copied' ? 'Copied code' : 'Copy code'}
              title={copyStatus === 'failed' ? 'Copy failed' : copyStatus === 'copied' ? 'Copied' : 'Copy code'}
            >
              {copyStatus === 'copied' ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        ) : null}
        <div className="arrow-code-body">
          {!languageLabel ? (
            <button
              type="button"
              onClick={handleCopy}
              className="arrow-code-copy-button"
              aria-label={copyStatus === 'copied' ? 'Copied code' : 'Copy code'}
              title={copyStatus === 'failed' ? 'Copy failed' : copyStatus === 'copied' ? 'Copied' : 'Copy code'}
            >
              {copyStatus === 'copied' ? <Check size={18} /> : <Copy size={18} />}
            </button>
          ) : null}
          <SyntaxHighlighter
            style={syntaxTheme}
            language={language || 'text'}
            PreTag="div"
            className="arrow-code-syntax"
            customStyle={{ margin: 0, padding: 0, background: 'transparent' }}
            codeTagProps={{ style: { background: 'transparent', textShadow: 'none' } }}
            wrapLongLines={false}
            {...props}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      </div>
    );
  } else {
    return (
      <code
        className="arrow-inline-code"
        {...props}
      >
        {children}
      </code>
    );
  }
};

export default CodeBlock;
